/**
 * Centered Footer
 * 
 * Centered content with minimal columns.
 * Balanced and visually appealing.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Facebook, 
  Instagram, 
  Twitter, 
  Youtube,
  Linkedin,
  ArrowRight,
} from "lucide-react";
import { siteConfig, getEnabledSocialLinks, type SocialPlatform } from "@/lib/site-config";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { newsletterService } from "@/services/newsletterService";
import { toast } from "sonner";
import { RichTextDisplay } from "@/components/ui/rich-text-display";

const socialIcons: Record<SocialPlatform, React.ComponentType<{ className?: string }>> = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
  tiktok: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
};

const FooterCentered = () => {
  const currentYear = new Date().getFullYear();
  const config = useSiteConfig();
  const { features } = useSiteSettings();
  const { currentLayout } = useLayout();
  const footerConfig = currentLayout.footer;
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const enabledSocialLinks = getEnabledSocialLinks(config.socialLinks);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about-us" },
    ...(features.programs.enabled ? [{ name: "Programs", path: "/programs" }] : []),
    ...(features.events.enabled ? [{ name: "Events", path: "/events" }] : []),
    ...(features.gallery.enabled ? [{ name: "Gallery", path: "/gallery" }] : []),
    ...(features.news.enabled ? [{ name: "News", path: "/news" }] : []),
    { name: "Contact", path: "/contact" },
  ];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await newsletterService.subscribeToNewsletter({
        email: email.trim(),
      });

      if (result.success) {
        toast.success('Thank you for subscribing to our newsletter!');
        setEmail('');
      } else {
        toast.error(result.error?.getUserMessage() || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Logo and Description */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <span className="text-2xl font-bold">{config.organizationName}</span>
          </div>
          <div className="text-primary-foreground/80 max-w-md mx-auto [&_.prose]:text-primary-foreground/80">
            <RichTextDisplay content={config.description || ''} prose={false} />
          </div>
        </div>

        {/* Navigation Links */}
        {footerConfig.showQuickLinks && (
          <nav className="flex flex-wrap justify-center gap-6 mb-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-primary-foreground/80 hover:text-heritage transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </nav>
        )}

        {/* Newsletter */}
        {footerConfig.showNewsletter && features.newsletter.enabled && (
          <div className="text-center mb-8">
            <h3 className="font-semibold mb-4">{features.newsletter.title}</h3>
            <form onSubmit={handleNewsletterSubmit} className="flex max-w-md mx-auto gap-2">
              <Input 
                type="email"
                placeholder="Enter your email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/60"
                aria-label="Email address for newsletter"
                required
              />
              <Button 
                type="submit"
                variant="heritage" 
                disabled={isSubmitting}
                aria-label="Subscribe to newsletter"
              >
                {isSubmitting ? (
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Social Icons */}
        {footerConfig.showSocialLinks && enabledSocialLinks.length > 0 && (
          <div className="flex justify-center space-x-4 mb-8">
            {enabledSocialLinks.map((link) => {
              const IconComponent = socialIcons[link.platform];
              return (
                <a 
                  key={link.platform}
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-primary-foreground/10 hover:bg-heritage/20 rounded-full flex items-center justify-center transition-colors"
                  aria-label={`Follow us on ${link.platform}`}
                >
                  <IconComponent className="w-5 h-5" />
                </a>
              );
            })}
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-primary-foreground/20 bg-primary-darker pt-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-sm text-primary-foreground/60">
            <span>© {currentYear} {config.organizationName}. All rights reserved.</span>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-heritage transition-colors">
                Privacy
              </Link>
              <Link to="/accessibility" className="hover:text-heritage transition-colors">
                Accessibility
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterCentered;


